import { CheckIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { TeacherTestDetails } from "p6m-p6u";
import { type FormEvent, useEffect, useState } from "react";
import InlineFormError from "../../basic/InlineFormError";
import PDFDownloadButton, { PDFDownloadButtonVariants } from "../../basic/pdfBasic/PDFDownloadButton/PDFDownloadButton";
import LinkButton from "../../basic/LinkButton";
import SsoButton from "../../basic/SsoButton";
import TextInput, { TextInputTypes } from "../../basic/TextInput";
import { useLogger } from "../../hooks/useLogger";
import { ANONYMOUS_FLOW_ANALYTICS_SOURCE } from "../../constants/AnonymousFlowConstants";
import type { SsoProvider } from "../../constants/SsoConstants";
import { RegistrationOutcomes } from "../../networking/registration";
import { AreaButton, ButtonVariant } from "../teacherArea/AreaButton";
import AnonymousFlowPopupFrame from "./AnonymousFlowPopupFrame";
import DecorativeTestSheet from "./DecorativeTestSheet";

interface TestReadyPopupProps {
    isOpen: boolean;
    onClose: () => void;
    testDetails?: TeacherTestDetails;
    /** Reports how the registration went; rejects when the save that follows a successful one failed. */
    onRegister: (email: string) => Promise<RegistrationOutcomes>;
    /** Saves the test and hands the teacher over to the phase-6 login; rejects when the save failed. */
    onOpenLogin: () => Promise<void>;
    /** Saves the test and hands the teacher over to Google/Apple/fobizz; rejects when the save failed. */
    onSsoRegister: (provider: SsoProvider) => Promise<void>;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TITLE_ID = "test-ready-popup-title";

/** First popup of the anonymous flow: the test is done, nudge towards creating an account (PDF stays available). */
const TestReadyPopup = (props: TestReadyPopupProps) => {
    const t = useT();
    const { logEvent } = useLogger();

    const t_title = t("Save your test with a free account", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_benefitAdapt = t("Adapt it for the parallel class instead of rebuilding it", {
        _tags: "CreateTest, AnonymousSavePopup",
    });
    const t_benefitOnline = t("Let students answer online and evaluate their answers in no time", {
        _tags: "CreateTest, AnonymousSavePopup",
    });
    const t_benefitFree = t("Free, no subscription, no payment details", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_emailPlaceholder = t("name@school.de", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_createAccount = t("Create account", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_registerFailed = t("Creating the account didn't work. Please try again.", {
        _tags: "CreateTest, AnonymousSavePopup",
    });
    const t_emailAlreadyExists = t("This email already has a phase6 account. Please log in instead.", {
        _tags: "CreateTest, AnonymousSavePopup",
    });
    const t_sessionFailed = t(
        "Your account was created, but logging you in didn't work. Please log in with the password we emailed you.",
        { _tags: "CreateTest, AnonymousSavePopup" }
    );
    const t_saveFailed = t("Saving the test didn't work. Please try again.", {
        _tags: "CreateTest, AnonymousSavePopup",
    });
    const t_alreadyHaveAccount = t("Already have an account?", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_login = t("Log in", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_downloadWithoutAccount = t("Download PDF without an account", { _tags: "CreateTest, AnonymousSavePopup" });
    // Only these wordings are allowed by the Google/Apple branding guidelines (de: "Weiter mit Google",
    // "Weiter mit Apple"); a bare "Google" / "Apple" is not.
    const t_continueWithGoogle = t("Continue with Google", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_continueWithApple = t("Continue with Apple", { _tags: "CreateTest, AnonymousSavePopup" });
    // Same wording as the website's fobizz button (de: "Weiter mit fobizz"); fobizz is written in lowercase.
    const t_continueWithFobizz = t("Continue with fobizz", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_orWithEmail = t("or with e-mail", { _tags: "CreateTest, AnonymousSavePopup" });
    const t_sr_email = t("Email address", { _tags: "CreateTest, AnonymousSavePopup, ScreenReaderOnly" });
    const t_exercisePDF = t("Exercise Sheet", { _tags: "CreateTest,PDFCreation" });
    const t_test = t("Test", { _tags: "CreateTest,PDFCreation" });

    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Keeps the popup locked until the provider redirect unloads the page.
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const isBusy = isSubmitting || isRedirecting;

    // Back from the Google/Apple/fobizz page, the browser may restore this page from its back/forward cache with the
    // lock still set; without the reset every button stays disabled and the popup can't be closed.
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) setIsRedirecting(false);
        };
        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    }, []);
    const isEmailValid = EMAIL_PATTERN.test(email.trim());
    const benefits = [t_benefitAdapt, t_benefitOnline, t_benefitFree];
    const testName = props.testDetails?.name ?? t_exercisePDF;

    /**
     * Shared busy state and error slot for all actions. The action returns the message to show, or "" when
     * it worked; only the test save can throw, so a rejection means that failed. For registration that save
     * runs after the account exists, so a retry there reports EMAIL_ALREADY_EXISTS.
     */
    const runAndReportFailure = async (action: () => Promise<string>) => {
        if (isBusy) return;

        setIsSubmitting(true);
        setErrorMessage("");
        try {
            setErrorMessage(await action());
        } catch (error) {
            console.warn("Anonymous save flow action failed", error);
            setErrorMessage(t_saveFailed);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isEmailValid) return;

        logEvent("Register Button Clicked", { source: ANONYMOUS_FLOW_ANALYTICS_SOURCE });
        runAndReportFailure(async () => {
            const outcome = await props.onRegister(email.trim());
            if (outcome === RegistrationOutcomes.EMAIL_ALREADY_EXISTS) return t_emailAlreadyExists;
            if (outcome === RegistrationOutcomes.SESSION_FAILED) return t_sessionFailed;
            return outcome === RegistrationOutcomes.FAILED ? t_registerFailed : "";
        });
    };

    const handleOpenLogin = () => {
        runAndReportFailure(async () => {
            await props.onOpenLogin();
            return "";
        });
    };

    const handleSsoRegister = (provider: SsoProvider) => {
        runAndReportFailure(async () => {
            await props.onSsoRegister(provider);
            setIsRedirecting(true);
            return "";
        });
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        if (errorMessage) setErrorMessage("");
    };

    // Three fanned "pages" of the test with the green "done" badge.
    const renderSheetStack = () => {
        const backSheetClasses =
            "absolute bottom-[0] left-[14px] w-[92px] h-[118px] bg-white border border-p6-grey-lightest rounded-xsmall-container";
        return (
            <div
                aria-hidden="true"
                className="relative w-[120px] h-[124px] mb-xs"
            >
                <span className={`${backSheetClasses} -rotate-10 -translate-x-[8px]`} />
                <span className={`${backSheetClasses} rotate-10 translate-x-[8px]`} />
                <DecorativeTestSheet
                    name={testName}
                    className="absolute bottom-[0] left-[14px]"
                />
                <span className="absolute right-[6px] -bottom-[4px] flex items-center justify-center w-[28px] h-[28px] rounded-circle bg-p6-green text-white border-[3px] border-white box-content">
                    <CheckIcon
                        size={16}
                        weight="bold"
                    />
                </span>
            </div>
        );
    };

    const renderBenefit = (text: string) => (
        <li
            key={text}
            className="flex items-start gap-xs"
        >
            <CheckIcon
                size={18}
                weight="bold"
                className="text-p6-green shrink-0 mt-[3px]"
                aria-hidden="true"
            />
            <span className="text-l text-p6-grey-dark leading-snug">{text}</span>
        </li>
    );

    return (
        <AnonymousFlowPopupFrame
            isOpen={props.isOpen}
            onClose={props.onClose}
            isSubmitting={isBusy}
            titleId={TITLE_ID}
            data-testid="anonymous-save-test-modal"
        >
            <div className="flex flex-col items-center gap-xs text-center px-s">
                {renderSheetStack()}
                <h2
                    id={TITLE_ID}
                    className="text-xl font-semibold text-p6-grey-dark leading-tight"
                >
                    {t_title}
                </h2>
            </div>

            <ul className="flex flex-col gap-xs list-none">{benefits.map(renderBenefit)}</ul>

            <div className="flex flex-col gap-xs">
                <SsoButton
                    data-testid="button-anonymous-sso-apple"
                    provider="apple"
                    label={t_continueWithApple}
                    onClick={() => handleSsoRegister("apple")}
                    disabled={isBusy}
                />
                <SsoButton
                    data-testid="button-anonymous-sso-google"
                    provider="google"
                    label={t_continueWithGoogle}
                    onClick={() => handleSsoRegister("google")}
                    disabled={isBusy}
                />
                <SsoButton
                    data-testid="button-anonymous-sso-fobizz"
                    provider="fobizz"
                    label={t_continueWithFobizz}
                    onClick={() => handleSsoRegister("fobizz")}
                    disabled={isBusy}
                />
            </div>

            <div className="flex items-center gap-s text-s text-p6-grey-mid">
                <span className="flex-1 border-t border-p6-grey-lightest" />
                {t_orWithEmail}
                <span className="flex-1 border-t border-p6-grey-lightest" />
            </div>

            <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-xs"
            >
                <TextInput
                    aria-label={t_sr_email}
                    data-testid="input-anonymous-register-email"
                    type={TextInputTypes.EMAIL}
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t_emailPlaceholder}
                    disabled={isBusy}
                    hasError={!!errorMessage}
                />
                {errorMessage && (
                    <InlineFormError data-testid="anonymous-register-error">{errorMessage}</InlineFormError>
                )}
                <AreaButton
                    data-testid="button-anonymous-register"
                    role="button"
                    buttonType="submit"
                    variant={ButtonVariant.PRIMARY}
                    className="w-full"
                    disabled={!isEmailValid || isBusy}
                >
                    {t_createAccount}
                </AreaButton>
                <p className="text-m text-p6-grey-dark text-center">
                    {t_alreadyHaveAccount}{" "}
                    <LinkButton
                        data-testid="button-anonymous-open-login"
                        onClick={handleOpenLogin}
                        disabled={isBusy}
                    >
                        {t_login}
                    </LinkButton>
                </p>
            </form>

            <div className="self-center">
                <PDFDownloadButton
                    variant={PDFDownloadButtonVariants.TEXT_LINK}
                    buttonText={t_downloadWithoutAccount}
                    fileName={`${testName} ${t_test}.pdf`}
                    testDetails={props.testDetails}
                    analyticsSource={ANONYMOUS_FLOW_ANALYTICS_SOURCE}
                    // Kept next to the shared download event: existing Amplitude charts are built on this one.
                    onDownloadClick={() => logEvent("Anonymous Test Saved As PDF")}
                />
            </div>
        </AnonymousFlowPopupFrame>
    );
};

export default TestReadyPopup;
